//addcamera.js
import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, Button, Form, Input, message } from "antd";
import {
  ArrowLeft,
  Camera,
  Lock,
  Info,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  Wifi,
  Server,
  Eye, ChevronRight
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "../../../utils/axios";
import Header from "../../header/header2";

// ==========================================================================
// CONSTANTS — lifted out of component so they are never re-created
// ==========================================================================

/**
 * RTSP URL templates.
 *
 * Hikvision:
 *   Direct  → /Streaming/channels/{ch}01/main   (ch = user-entered, default 1)
 *   NVR     → /Streaming/channels/{ch}01/main   (ch = 100,200,300… i.e. user ch × 100)
 *
 * Dahua:
 *   Direct  → /cam/realtime?channel=0&subtype=0  (always channel 0 for single cam)
 *   NVR     → /cam/realtime?channel={ch-1}&subtype=0  (0-indexed)
 */
function buildRtspUrl({ brand, connectionType, ip, port, channel, cameraUsername, password }) {
  const safePort = port || 554;
  const auth = `${cameraUsername || "admin"}:${password || ""}`;
  const base = `rtsp://${auth}@${ip}:${safePort}`;

  if (brand === "Hikvision") {
    // Hikvision NVR uses channel × 100; direct camera just uses the channel as-is
    const ch = connectionType === "nvr" ? Number(channel) : Number(channel || 1);
    return `${base}/Streaming/channels/${ch}01/main`;
  }

  // Dahua — channel is 0-indexed
  // const ch = connectionType === "nvr" ? Number(channel || 1) : 0;
  // return `${base}/cam/realmonitor?channel=${ch}&subtype=0`;
  const ch =
    connectionType === "nvr"
      ? Number(channel || 1) - 1 // NVR: 1->0, 2->1...
      : 1; // Direct camera uses channel=1

  return `${base}/cam/realmonitor?channel=${ch}&subtype=0`;
}

/** Default port for RTSP across both brands */
const DEFAULT_PORT = "554";

// Validation rules — defined once, outside the component
const requiredRule = { required: true, message: "This field is required" };

const ipRules = [
  requiredRule,
  {
    pattern: /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)$/,
    message: "Enter a valid IP address (e.g. 192.168.1.100)",
  },
];

const portRules = [
  requiredRule,
  {
    validator: (_, value) => {
      if (!value && value !== 0) return Promise.resolve();
      const num = Number(value);
      if (isNaN(num) || num < 1 || num > 65535)
        return Promise.reject(new Error("Port must be 1–65535"));
      return Promise.resolve();
    },
  },
];

const nameRules = [
  requiredRule,
  { min: 3, message: "At least 3 characters" },
  { max: 50, message: "Max 50 characters" },
];

const passwordRules = [
  requiredRule,
  { min: 4, message: "At least 4 characters" },
];

const cameraUsernameRules = [
  requiredRule,
  { min: 2, message: "At least 2 characters" },
];

const channelRules = [
  requiredRule,
  {
    pattern: /^[1-9]\d*$/,
    message: "Must be a positive integer (1–32 typical)",
  },
];

// ==========================================================================
// HELP CONTENT — rendered inside the collapsible panel
// ==========================================================================

function HelpPanel({ connectionType, brand }) {
  const [open, setOpen] = useState(false);

  const directHelp = (
    <div className="space-y-2 text-sm text-slate-600">
      <p className="flex items-start gap-2">
        <Wifi className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
        <span>
          <strong className="text-slate-700">IP Address</strong> — Enter the
          camera's own IP address. Each camera has its own unique IP on your
          network.
        </span>
      </p>
      <p className="flex items-start gap-2">
        <span className="w-4 h-4 shrink-0" /> {/* spacer to align */}
        <span>
          <strong className="text-slate-700">Port</strong> — Default RTSP port
          is <code className="bg-slate-100 px-1 rounded">554</code>. Only
          change this if your camera uses a custom port.
        </span>
      </p>
      <p className="flex items-start gap-2">
        <span className="w-4 h-4 shrink-0" />
        <span>
          <strong className="text-slate-700">Channel</strong> — Not required
          for a direct camera connection; it is auto-set to 1.
        </span>
      </p>
      <p className="flex items-start gap-2">
        <span className="w-4 h-4 shrink-0" />
        <span>
          <strong className="text-slate-700">Username / Password</strong> —
          These are the credentials you set on the camera itself (not your
          account credentials).
        </span>
      </p>
    </div>
  );

  const nvrHelp = (
    <div className="space-y-2 text-sm text-slate-600">
      <p className="flex items-start gap-2">
        <Server className="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
        <span>
          <strong className="text-slate-700">IP Address</strong> — Enter the
          NVR / DVR's IP address. All cameras connected to the same NVR share
          this IP.
        </span>
      </p>
      <p className="flex items-start gap-2">
        <span className="w-4 h-4 shrink-0" />
        <span>
          <strong className="text-slate-700">Channel</strong> — Each physical
          camera plugged into the NVR occupies a unique channel (1, 2, 3…).{" "}
          <strong className="text-red-600">
            Two cameras on the same NVR cannot share a channel number.
          </strong>{" "}
          Check the label on the back of your NVR or its admin panel.
        </span>
      </p>
      <p className="flex items-start gap-2">
        <span className="w-4 h-4 shrink-0" />
        <span>
          <strong className="text-slate-700">Port</strong> — Default is{" "}
          <code className="bg-slate-100 px-1 rounded">554</code>. Use the same
          port for every camera on the same NVR unless explicitly told otherwise.
        </span>
      </p>
      <p className="flex items-start gap-2">
        <span className="w-4 h-4 shrink-0" />
        <span>
          <strong className="text-slate-700">Username / Password</strong> —
          Use the NVR admin credentials (the same for every channel on that
          NVR).
        </span>
      </p>
      {brand && (
        <p className="flex items-start gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
          <span className="w-4 h-4 shrink-0" />
          <span>
            <strong className="text-indigo-700">{brand} NVR note:</strong>{" "}
            {brand === "Hikvision"
              ? "Hikvision NVRs map channels internally as channel × 100 (e.g. channel 1 → 100). You just enter 1, 2, 3… and the URL is built automatically."
              : "Dahua NVRs use 0-indexed channels internally. Enter 1, 2, 3… as you see them on the NVR UI — the conversion is handled automatically."}
          </span>
        </p>
      )}
    </div>
  );

  return (
    <div className="border border-blue-200 bg-blue-50/60 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-blue-700 hover:bg-blue-100/50 transition-colors"
      >
        <span className="flex items-center gap-2 text-sm font-semibold">
          <Info className="w-4 h-4" />
          How to fill in the details
          {connectionType === "nvr" && (
            <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">
              NVR mode
            </span>
          )}
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && <div className="px-4 pb-4">{connectionType === "nvr" ? nvrHelp : directHelp}</div>}
    </div>
  );
}

// ==========================================================================
// RTSP PREVIEW — live URL preview that updates as the user types
// ==========================================================================

function RtspPreview({ brand, connectionType, values }) {
  const { ip, port, channel, cameraUsername, password } = values || {};
  if (!ip) return null; // don't show preview until user has typed an IP

  const url = buildRtspUrl({ brand, connectionType, ip, port, channel, cameraUsername, password });
  // Mask password in the displayed URL
  const masked = url.replace(/:([^@]+)@/, ":*****@");

  return (
    <div className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
      <Eye className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <span className="text-xs text-slate-400 font-semibold uppercase tracking-wide">
          RTSP Preview
        </span>
        <code className="block text-xs text-slate-700 break-all mt-0.5">{masked}</code>
      </div>
    </div>
  );
}

// ==========================================================================
// VALIDATION STATUS BADGE
// ==========================================================================

function ValidationBadge({ status, error }) {
  if (status === "idle") return null;

  const map = {
    validating: { icon: <Loader2 className="w-4 h-4 animate-spin text-blue-500" />, label: "Validating…", cls: "text-blue-600 bg-blue-50 border-blue-200" },
    valid: { icon: <CheckCircle2 className="w-4 h-4 text-green-600" />, label: "Stream validated", cls: "text-green-700 bg-green-50 border-green-200" },
    invalid: { icon: <XCircle className="w-4 h-4 text-red-500" />, label: error || "Validation failed", cls: "text-red-700 bg-red-50 border-red-200" },
  };

  const { icon, label, cls } = map[status] || {};
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium ${cls}`}>
      {icon}
      <span>{label}</span>
    </div>
  );
}

// ==========================================================================
// DUPLICATE-CHANNEL WARNING (shown inline on the channel input)
// ==========================================================================

function DuplicateWarning() {
  return (
    <div className="flex items-center gap-1.5 mt-1 text-xs text-amber-700 font-medium">
      <AlertTriangle className="w-3.5 h-3.5" />
      This channel is already used on this NVR IP. Pick a different one.
    </div>
  );
}

// ==========================================================================
// MAIN COMPONENT
// ==========================================================================

const AddCamera = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const citriot = JSON.parse(localStorage.getItem("citriot")) || {};

  // ── derive brand + projectName from navigation state ────────────────────
  // FIX: SelectCamera sends { selectedCamera: { company, … } }.
  //      We read company here and store it so every card knows the brand.
  const [brand, setBrand] = useState("");

  const projectname =
    location.state?.projectName ||
    localStorage.getItem("projectname") ||
    "";
  const organizationId = localStorage.getItem("organizationId");
  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}"); }
    catch { return {}; }
  }, []);

  const username = user?.username || "";
  const cafeId = localStorage.getItem("cafeId");

  // ── per-card state that lives OUTSIDE antd Form (validation + connection type) ─
  // key = field-list index
  const [connectionTypes, setConnectionTypes] = useState({ 0: "direct" });
  const [validationMap, setValidationMap]     = useState({}); // { idx: { status, error } }

  // ── helpers ──────────────────────────────────────────────────────────────

  /** Read current form cameras array */
  const getCameras = useCallback(() => form.getFieldValue("cameras") || [], [form]);

  /** Detect duplicate (ip + channel) pairs across cards in NVR mode */
  const getDuplicateChannels = useCallback(() => {
    const cameras = getCameras();
    const seen = {}; // key: `ip:channel` → count
    const dupes = new Set();
    cameras.forEach((cam, idx) => {
      if (connectionTypes[idx] !== "nvr" || !cam?.ip || !cam?.channel) return;
      const key = `${cam.ip.trim()}:${cam.channel}`;
      seen[key] = (seen[key] || 0) + 1;
      if (seen[key] > 1) dupes.add(key);
    });
    // return set of indices that collide
    const dupeIndices = new Set();
    cameras.forEach((cam, idx) => {
      if (connectionTypes[idx] !== "nvr" || !cam?.ip || !cam?.channel) return;
      if (dupes.has(`${cam.ip.trim()}:${cam.channel}`)) dupeIndices.add(idx);
    });
    return dupeIndices;
  }, [getCameras, connectionTypes]);

  // ── seed projectName into every new camera entry ────────────────────────
  useEffect(() => {
    const cameras = getCameras();
    if (!cameras.length) return;
    form.setFieldsValue({
      cameras: cameras.map((cam) => ({ ...cam, projectName: projectname, port: cam.port || DEFAULT_PORT })),
    });
  }, [projectname, form, getCameras]);

  // ── RTSP validation — sequential, one card at a time ────────────────────

  /**
   * Validate a single camera card against the backend.
   * Returns true if valid, false otherwise.
   */
  const validateSingleCamera = useCallback(async (idx, cam) => {
    setValidationMap((prev) => ({ ...prev, [idx]: { status: "validating", error: null } }));

    const connType = connectionTypes[idx] || "direct";
    const rtspUrl = buildRtspUrl({
      brand,
      connectionType: connType,
      ip: cam.ip?.trim(),
      port: cam.port,
      channel: cam.channel,
      cameraUsername: cam.cameraUsername,
      password: cam.password,
    });

    try {
      const res = await axios.post("/validate-rtsp", {
        rtspUrl,
        cameraName: cam.name?.trim(),
        projectName: cam.projectName?.trim(),
      });

      if (res.data.success && res.data.valid) {
        setValidationMap((prev) => ({ ...prev, [idx]: { status: "valid", error: null } }));
        return true;
      }
      // backend said invalid
      const errMsg = res.data.message || "Stream is not accessible";
      setValidationMap((prev) => ({ ...prev, [idx]: { status: "invalid", error: errMsg } }));
      message.error(`Camera ${idx + 1}: ${errMsg}`);
      return false;
    } catch (err) {
      const errMsg = err.response?.data?.message || "Could not reach validation service";
      setValidationMap((prev) => ({ ...prev, [idx]: { status: "invalid", error: errMsg } }));
      message.error(`Camera ${idx + 1}: ${errMsg}`);
      return false;
    }
  }, [brand, connectionTypes]);

  const fetchProjects = async () => {
    try {
      const res = await axios.post("/fetch-createprojects", {
        username,
        organizationId,
      });

      if (res.data.success) {
        const project = res.data.data.find(
          (p) => p.projectname === projectname
        );

        if (project?.selectedCameraModel?.length > 0) {
          // modelname = brand
          setBrand(project.selectedCameraModel[0].modelname);
        }
      }
    } catch (error) {
      console.error("Failed to fetch project:", error);
    }
  };

  useEffect(() => {
    if (username && organizationId && projectname) {
      fetchProjects();
    }
  }, [username, organizationId, projectname]);

  // ── SUBMIT — validate every card sequentially, then persist ─────────────

  const handleSubmit = useCallback(async (values) => {
    const cameras = values.cameras || [];
    const dupeIndices = getDuplicateChannels();

    // ── 1. Block if any NVR duplicate channels exist ──────────────────
    if (dupeIndices.size > 0) {
      message.warning("Fix duplicate channel numbers on the same NVR before saving.");
      return;
    }

    // ── 2. Sequential RTSP validation ──────────────────────────────────
    for (let i = 0; i < cameras.length; i++) {
      const valid = await validateSingleCamera(i, cameras[i]);
      if (!valid) return; // stop — don't validate or save further cameras
    }

    // ── 3. All valid → persist one by one (sequential, clear status on success) ─
    for (let i = 0; i < cameras.length; i++) {
      try {
        const cam = cameras[i];
        const payload = {
          user: username,
          organizationId,
          cafeId,
          password: cam.password,
          cameraName: cam.name?.trim(),
          ipAddress: cam.ip?.trim(),
          channel: Number(cam.channel || 1),
          port: Number(cam.port || 554),
          projectName: cam.projectName?.trim(),
          location: cam.location?.trim(),
          username: cam.cameraUsername,
          connectionType: connectionTypes[i] || "direct",
          brand,
        };

        const res = await axios.post("/add-camera", payload);
        if (!res.data.success) {
          message.error(`Camera ${i + 1}: ${res.data.message || "Save failed"}`);
          return;
        }
      } catch (err) {
        message.error(`Camera ${i + 1}: ${err.response?.data?.message || "Network error"}`);
        return;
      }
    }

    message.success("All cameras added successfully");
    form.resetFields();
    setValidationMap({});
    setConnectionTypes({ 0: "direct" });
    navigate("/selectmodel", { state: {projectname: projectname}});
  }, [getDuplicateChannels, validateSingleCamera, username, cafeId, connectionTypes, brand, form, navigate]);

  // ── render ─────────────────────────────────────────────────────────────

  const dupeIndices = getDuplicateChannels();

  return (
    <>
      <Header auth={citriot?.auth}/>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* Breadcrumb */}
          {/* Breadcrumb + Edit Use Cases */}
         {/* Breadcrumb + Edit Use Cases */}
<div className="flex items-center justify-between mb-5">
  <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
    
    {/* Step 1 — Select Project */}
    <button
      onClick={() => navigate("/create-project")}
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors text-sm font-medium cursor-pointer border-none bg-transparent"
    >
      <span className="w-5 h-5 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-bold shrink-0">1</span>
      <span className="hidden sm:inline">Select Project</span>
    </button>

    <ChevronRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />

    {/* Step 2 — Use Case */}
    <button
      onClick={() => navigate("/home")}
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-violet-600 hover:bg-violet-50 transition-colors text-sm font-medium cursor-pointer border-none bg-transparent"
    >
      <span className="w-5 h-5 rounded-full bg-violet-500 text-white text-xs flex items-center justify-center font-bold shrink-0">2</span>
      <span className="hidden sm:inline">Use Case</span>
    </button>

    <ChevronRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />

    {/* Step 3 — Select Camera */}
    <button
      onClick={() => navigate("/selectcamera")}
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors text-sm font-medium cursor-pointer border-none bg-transparent"
    >
      <span className="w-5 h-5 rounded-full bg-emerald-500 text-white text-xs flex items-center justify-center font-bold shrink-0">3</span>
      <span className="hidden sm:inline">Select Camera</span>
    </button>

    <ChevronRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />

    {/* Step 4 — Add Camera (current) */}
    <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-orange-50 text-orange-600 text-sm font-semibold">
      <span className="w-5 h-5 rounded-full bg-orange-400 text-white text-xs flex items-center justify-center font-bold shrink-0">4</span>
      <span className="hidden sm:inline">Add Camera</span>
    </span>

  </div>
</div>

          {/* Page header card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md shrink-0">
              <Camera className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold text-gray-900">Add New Camera(s)</h1>
                {brand && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    brand === "Hikvision" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"
                  }`}>
                    {brand}
                  </span>
                )}
              </div>
              <p className="text-gray-500 text-sm mt-0.5">
                Configure and validate each camera's RTSP stream before saving
              </p>
            </div>
          </div>

          {/* FORM */}
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              cameras: [{ name: "", projectName: projectname, location: "", ip: "", port: DEFAULT_PORT, channel: "", cameraUsername: "", password: "" }],
            }}
          >
            {/* ── Auth info banner (read-only, informational) ────────────── */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 mb-4 flex items-center gap-3">
              <Lock className="w-4 h-4 text-slate-500 shrink-0" />
              <span className="text-sm text-slate-600">
                Logged in as <strong className="text-slate-800">{username}</strong> ·
                Project <strong className="text-slate-800">{projectname || "—"}</strong>
              </span>
            </div>

            {/* ── Multi-camera Form.List ────────────────────────────────── */}
            <Form.List name="cameras">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name: fieldIdx, ...restField }) => {
                    const connType = connectionTypes[fieldIdx] ?? "direct";
                    const camValues = form.getFieldValue(["cameras", fieldIdx]) || {};
                    const hasDupe = dupeIndices.has(fieldIdx);
                    const valStatus = validationMap[fieldIdx];

                    return (
                      <Card
                        key={key}
                        className="mb-4 rounded-xl shadow-sm border"
                        styles={{ header: { padding: "12px 16px" } }}
                        title={
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-bold text-slate-800">
                              Camera {fieldIdx + 1}
                            </span>
                            {valStatus && <ValidationBadge status={valStatus.status} error={valStatus.error} />}
                          </div>
                        }
                        extra={
                          <div className="flex items-center gap-2">
                            {fields.length > 1 && (
                              <Button
                                danger
                                size="small"
                                onClick={() => {
                                  remove(fieldIdx);
                                  // shift connectionTypes and validationMap keys
                                  setConnectionTypes((prev) => {
                                    const next = {};
                                    Object.keys(prev).forEach((k) => {
                                      const n = Number(k);
                                      if (n < fieldIdx) next[n] = prev[k];
                                      else if (n > fieldIdx) next[n - 1] = prev[k];
                                    });
                                    return next;
                                  });
                                  setValidationMap((prev) => {
                                    const next = {};
                                    Object.keys(prev).forEach((k) => {
                                      const n = Number(k);
                                      if (n < fieldIdx) next[n] = prev[k];
                                      else if (n > fieldIdx) next[n - 1] = prev[k];
                                    });
                                    return next;
                                  });
                                }}
                              >
                                Remove
                              </Button>
                            )}
                          </div>
                        }
                      >
                        <div className="space-y-4">
                          {/* ── Connection-type toggle ────────────────────── */}
                          <div className="flex gap-2">
                            {[
                              { value: "direct", label: "Direct Camera", icon: <Wifi className="w-4 h-4" />, desc: "Camera has its own IP" },
                              { value: "nvr", label: "NVR / DVR", icon: <Server className="w-4 h-4" />, desc: "Camera is on a recorder" },
                            ].map((opt) => (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => {
                                  setConnectionTypes((prev) => ({ ...prev, [fieldIdx]: opt.value }));
                                  // reset validation when user changes connection type
                                  setValidationMap((prev) => { const n = { ...prev }; delete n[fieldIdx]; return n; });
                                }}
                                className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border transition-all text-left ${
                                  connType === opt.value
                                    ? "border-blue-500 bg-blue-50 text-blue-700"
                                    : "border-slate-200 bg-white text-slate-600 hover:border-blue-300"
                                }`}
                              >
                                {opt.icon}
                                <div>
                                  <div className="text-sm font-semibold">{opt.label}</div>
                                  <div className="text-xs text-slate-400">{opt.desc}</div>
                                </div>
                              </button>
                            ))}
                          </div>

                          {/* ── Help panel ────────────────────────────────── */}
                          <HelpPanel connectionType={connType} brand={brand} />

                          {/* ── Camera name + location row ────────────────── */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Form.Item
                              {...restField}
                              label="Camera Name"
                              name={[fieldIdx, "name"]}
                              rules={nameRules}
                            >
                              <Input placeholder="e.g. Main Entrance" />
                            </Form.Item>

                            <Form.Item
                              {...restField}
                              label="Location"
                              name={[fieldIdx, "location"]}
                              rules={nameRules}
                            >
                              <Input placeholder="e.g. Gate 1, Floor 2" />
                            </Form.Item>
                          </div>

                          {/* Project name (disabled, auto-filled) */}
                          <Form.Item
                            {...restField}
                            label="Project Name"
                            name={[fieldIdx, "projectName"]}
                          >
                            <Input disabled placeholder={projectname || "—"} />
                          </Form.Item>

                          {/* ── Network row ───────────────────────────────── */}
                          <div className={`grid gap-4 ${connType === "nvr" ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1 md:grid-cols-2"}`}>
                            <Form.Item
                              {...restField}
                              label={connType === "nvr" ? "NVR / DVR IP Address" : "Camera IP Address"}
                              name={[fieldIdx, "ip"]}
                              rules={ipRules}
                            >
                              <Input placeholder="192.168.1.100" />
                            </Form.Item>

                            <Form.Item
                              {...restField}
                              label="Port"
                              name={[fieldIdx, "port"]}
                              rules={portRules}
                            >
                              <Input type="number" placeholder={DEFAULT_PORT} />
                            </Form.Item>

                            {/* Channel — visible always but behaviour differs */}
                            {connType === "nvr" && <Form.Item
                              {...restField}
                              label="Channel"
                              name={[fieldIdx, "channel"]}
                              rules={connType === "nvr" ? channelRules : []}
                              extra={hasDupe ? <DuplicateWarning /> : connType === "direct" ? <span className="text-xs text-slate-400">Auto-set for direct cameras</span> : null}
                            >
                              <Input
                                placeholder={connType === "nvr" ? "1" : "—"}
                                disabled={connType === "direct"}
                                className={hasDupe ? "border-amber-400 focus:border-amber-500" : ""}
                              />
                            </Form.Item>}
                          </div>

                          {/* ── Credentials row ───────────────────────────── */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Form.Item
                              {...restField}
                              label={connType === "nvr" ? "NVR Username" : "Camera Username"}
                              name={[fieldIdx, "cameraUsername"]}
                              rules={cameraUsernameRules}
                            >
                              <Input placeholder="admin" autoComplete="off" />
                            </Form.Item>

                            <Form.Item
                              {...restField}
                              label={connType === "nvr" ? "NVR Password" : "Camera Password"}
                              name={[fieldIdx, "password"]}
                              rules={passwordRules}
                            >
                              <Input.Password placeholder="Camera / NVR password" autoComplete="new-password" />
                            </Form.Item>
                          </div>

                          {/* ── Live RTSP preview ─────────────────────────── */}
                          <Form.Item dependencies={[
                            [fieldIdx, "ip"], [fieldIdx, "port"], [fieldIdx, "channel"],
                            [fieldIdx, "cameraUsername"], [fieldIdx, "password"],
                          ]}>
                            {({ getFieldValue }) => {
                              const vals = getFieldValue("cameras")?.[fieldIdx] || {};
                              return <RtspPreview brand={brand} connectionType={connType} values={vals} />;
                            }}
                          </Form.Item>
                        </div>
                      </Card>
                    );
                  })}

                  {/* Add-another button */}
                  <Button
                    type="dashed"
                    block
                    onClick={() => {
                      add({ name: "", projectName: projectname, location: "", ip: "", port: DEFAULT_PORT, channel: "", cameraUsername: "", password: "" });
                      setConnectionTypes((prev) => ({ ...prev, [fields.length]: "direct" }));
                    }}
                    className="mb-2"
                  >
                    + Add Another Camera
                  </Button>
                </>
              )}
            </Form.List>

            {/* ── Action buttons ──────────────────────────────────────── */}
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-200">
              <Button onClick={() => navigate(-1)} icon={<ArrowLeft size={15} />}>
                Back
              </Button>
              <Button type="primary" htmlType="submit" size="large" className="px-8">
                Validate & Save All Cameras
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </>
  );
};

export default AddCamera;